import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { FiSend, FiMessageSquare, FiX, FiSettings, FiMinimize2, FiMaximize2 } from 'react-icons/fi';
import * as personaService from '../../services/personaService';
import * as toolService from '../../services/toolService';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system' | 'error';
  content: string;
  timestamp: Date;
}

interface ChatWithCanvasProps {
  workflowData: any;
  onWorkflowUpdate: (updatedWorkflow: any) => void;
  show: boolean;
  onToggle: () => void;
  // Add support for programmatic message injection
  pendingMessage?: string;
  onMessageSent?: () => void;
}

export const ChatWithCanvas: React.FC<ChatWithCanvasProps> = ({
  workflowData,
  onWorkflowUpdate,
  show,
  onToggle,
  pendingMessage,
  onMessageSent
}) => {
  const { darkMode } = useTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoApply, setAutoApply] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [availablePersonas, setAvailablePersonas] = useState<any[]>([]);
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // COPIED FROM PersonaForge: Tinted background effect
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#10b981' : '#059669',
    enabled: true  // Enable beautiful tinted background effect
  });
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load available personas and tools when component mounts
  useEffect(() => {
    const loadPersonasAndTools = async () => {
      try {
        console.log('🗨️ CHAT-DEBUG: Loading available personas and tools...');
        
        // Load personas
        const personas = await personaService.getPersonas();
        const formattedPersonas = personas.map(persona => ({
          // Keep full name and ID - NEVER truncate these
          name: persona.name,
          id: persona.id, // Use ONLY the actual ID
          // Remove prompt entirely - not needed for workflow mutations (saves most tokens)
          tags: (persona.tags || []).slice(0, 10), // Max 10 tags
          // Only truncate descriptive text to save tokens
          specialization: (persona.shortDescription || persona.primarySpecialization || 'General purpose').substring(0, 50)
        }));
        setAvailablePersonas(formattedPersonas);
        console.log('🗨️ CHAT-DEBUG: Loaded personas:', formattedPersonas.length);
        
        // Load tools
        const tools = await toolService.getTools();
        console.log('🔧 TOOL-DEBUG: Raw tools from service:', tools.slice(0, 2));
        
        const formattedTools = tools.map(tool => ({
          // Keep full name and ID - NEVER truncate these
          name: tool.name,
          id: tool.name, // Use name as ID for tools
          // Keep essential metadata but truncate descriptions
          category: tool.metadata?.category || 'general',
          description: (tool.metadata?.documentation?.summary || 'No description').substring(0, 50),
          // Limit tags to save tokens
          tags: (tool.metadata?.tags || []).slice(0, 5), // Max 5 tags for tools
          // Keep version info short
          version: tool.metadata?.version ? 
            `${tool.metadata.version.major}.${tool.metadata.version.minor}.${tool.metadata.version.patch}` : 
            '1.0.0',
          // Add permission level for validation
          permission_level: tool.metadata?.permission_level || 'basic'
        }));
        setAvailableTools(formattedTools);
        console.log('🗨️ CHAT-DEBUG: Loaded tools:', formattedTools.length);
        console.log('🔧 TOOL-DEBUG: Formatted tools being sent:', formattedTools.slice(0, 2));
        console.log('🔧 TOOL-DEBUG: Tool data structure check:', {
          firstTool: formattedTools[0],
          hasName: !!formattedTools[0]?.name,
          hasId: !!formattedTools[0]?.id,
          hasCategory: !!formattedTools[0]?.category,
          hasDescription: !!formattedTools[0]?.description
        });
        
        // Calculate token savings for personas
        const originalPersonaSize = JSON.stringify(personas).length;
        const truncatedPersonaSize = JSON.stringify(formattedPersonas).length;
        const personaSavings = originalPersonaSize - truncatedPersonaSize;
        
        // Calculate token savings for tools
        const originalToolSize = JSON.stringify(tools).length;
        const truncatedToolSize = JSON.stringify(formattedTools).length;
        const toolSavings = originalToolSize - truncatedToolSize;
        
        console.log('🗨️ TOKEN-SAVINGS:', {
          personas: {
            originalChars: originalPersonaSize,
            truncatedChars: truncatedPersonaSize,
            savedChars: personaSavings,
            savedPercent: Math.round((personaSavings / originalPersonaSize) * 100) + '%'
          },
          tools: {
            originalChars: originalToolSize,
            truncatedChars: truncatedToolSize,
            savedChars: toolSavings,
            savedPercent: Math.round((toolSavings / originalToolSize) * 100) + '%'
          },
          total: {
            savedChars: personaSavings + toolSavings,
            totalOriginal: originalPersonaSize + originalToolSize,
            totalTruncated: truncatedPersonaSize + truncatedToolSize
          }
        });
      } catch (error) {
        console.error('🗨️ CHAT-DEBUG: Failed to load personas and tools:', error);
        setAvailablePersonas([]);
        setAvailableTools([]);
      }
    };
    
    if (show) {
      loadPersonasAndTools();
    }
  }, [show]);

  // Add welcome message when chat opens
  useEffect(() => {
    if (show && messages.length === 0) {
      setMessages([{
        id: Date.now().toString(),
        type: 'system',
        content: 'Hi! I can help you modify your workflow. Try saying things like:\n\n• "Add a code review phase"\n• "Connect brainstorming to synthesis"\n• "Add a web search tool"\n• "Include a system architect persona"',
        timestamp: new Date()
      }]);
    }
  }, [show, messages.length]);

  // Handle pending messages (for auto-fix functionality)
  useEffect(() => {
    if (pendingMessage && show && !isLoading) {
      console.log('🔧 CHAT-DEBUG: Received pending message:', pendingMessage);
      
      // Check if we have the necessary data loaded
      if (availablePersonas.length === 0 || availableTools.length === 0) {
        console.log('🔧 CHAT-DEBUG: Waiting for personas and tools to load before processing pending message...');
        // Don't process the message yet, wait for data to load
        return;
      }
      
      setInputValue(pendingMessage);
      
      // Auto-send the message after a brief delay
      setTimeout(() => {
        if (pendingMessage === inputValue) {
          console.log('🔧 CHAT-DEBUG: Auto-sending pending message with loaded data');
          handleSendMessage();
          onMessageSent?.();
        }
      }, 800);
    }
  }, [pendingMessage, show, isLoading, inputValue, onMessageSent, availablePersonas.length, availableTools.length]);

  // Add another effect to process pending messages when data becomes available
  useEffect(() => {
    if (pendingMessage && show && !isLoading && 
        availablePersonas.length > 0 && availableTools.length > 0 && 
        !inputValue) { // Only if we haven't set the input yet
      
      console.log('🔧 CHAT-DEBUG: Data loaded, processing pending message:', pendingMessage);
      setInputValue(pendingMessage);
      
      setTimeout(() => {
        console.log('🔧 CHAT-DEBUG: Auto-sending pending message after data load');
        handleSendMessage();
        onMessageSent?.();
      }, 800);
    }
  }, [availablePersonas.length, availableTools.length, pendingMessage, show, isLoading, inputValue, onMessageSent]);

  // Focus the input when chat is opened
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  useEffect(() => {
    if (show && !isMinimized && inputRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [show, isMinimized]);
  
  // Handle message sending with comprehensive logging
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;
    
    // Guard: Don't send if we don't have personas/tools loaded yet
    if (availablePersonas.length === 0 || availableTools.length === 0) {
      console.log('🗨️ CHAT-DEBUG: Blocking send - data not loaded yet');
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'error',
        content: 'Still loading personas and tools... please wait a moment.',
        timestamp: new Date()
      }]);
      return;
    }
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };
    
    console.log('🗨️ CHAT-DEBUG: Sending message:', {
      workflowId: workflowData?.id,
      message: inputValue,
      autoApply,
      availablePersonasCount: availablePersonas.length
    });
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    // Use the already-loaded personas and tools
    let currentPersonas = availablePersonas;
    let currentTools = availableTools;
    
    // Double-check we have data (should be caught by guard above, but safety check)
    if (currentPersonas.length === 0 || currentTools.length === 0) {
      console.error('🗨️ CHAT-DEBUG: No personas/tools available even after guard check');
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'error',
        content: 'Unable to load personas and tools. Please refresh the page.',
        timestamp: new Date()
      }]);
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('🗨️ CHAT-DEBUG: Making API call to:', `/api/workflows/${workflowData.id}/mutate`);
      console.log('🗨️ CHAT-DEBUG: Sending to API:', {
        workflowId: workflowData.id,
        availablePersonasCount: currentPersonas.length,
        availableToolsCount: currentTools.length,
        firstPersona: currentPersonas[0],
        firstTool: currentTools[0],
        requestPreview: {
          current_workflow: { id: workflowData.id, phases: workflowData.phases?.length },
          intent: userMessage.content,
          available_personas: currentPersonas.slice(0, 2), // Just first 2 for debugging
          available_tools: currentTools.slice(0, 2), // Just first 2 for debugging
          user_preferences: { auto_apply: autoApply, preserve_positions: true }
        }
      });
      
      const response = await fetch(`/api/workflows/${workflowData.id}/mutate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_workflow: workflowData,
          intent: userMessage.content,
          available_personas: currentPersonas,
          available_tools: currentTools,
          user_preferences: { 
            auto_apply: autoApply,
            preserve_positions: true 
          }
        })
      });
      
      console.log('🗨️ CHAT-DEBUG: API response status:', response.status);
      const result = await response.json();
      console.log('🗨️ CHAT-DEBUG: API response data:', result);
      
      if (result.success) {
        const updatedWorkflow = result.updated_workflow;

        // ── Client-side ghost node purge ──────────────────────────────────────
        // Belt-and-suspenders: remove any persona/tool nodes not backed by a phase assignment
        const usedPersonaIds = new Set<string>();
        const usedToolIds    = new Set<string>();
        (updatedWorkflow.phases ?? []).forEach((phase: any) => {
          (phase.config?.personas ?? []).forEach((pa: any) => {
            if (pa.persona_id) usedPersonaIds.add(pa.persona_id);
          });
          (phase.config?.tools ?? []).forEach((tid: string) => {
            usedToolIds.add(tid);
          });
        });

        // Build set of phase IDs that actually exist in the mutated workflow
        const existingPhaseIds = new Set<string>(
          (updatedWorkflow.phases ?? []).map((p: any) => p.id)
        );

        // Build set of node IDs that have at least one surviving connection
        const connectedNodeIds = new Set<string>();
        (updatedWorkflow.connections ?? []).forEach((c: any) => {
          connectedNodeIds.add(c.source);
          connectedNodeIds.add(c.target);
        });

        const ghostNodeIds = new Set<string>();
        updatedWorkflow.nodes = (updatedWorkflow.nodes ?? []).filter((node: any) => {
          if (node.type === 'persona') {
            const pid = node.data?.personaId;
            if (!pid || !usedPersonaIds.has(pid)) {
              console.warn(`🧹 CLIENT-PURGE: ghost persona node ${node.id} (personaId=${pid})`);
              ghostNodeIds.add(node.id);
              return false;
            }
          }
          if (node.type === 'tool') {
            const tid = node.data?.toolId;
            if (!tid || !usedToolIds.has(tid)) {
              console.warn(`🧹 CLIENT-PURGE: ghost tool node ${node.id} (toolId=${tid})`);
              ghostNodeIds.add(node.id);
              return false;
            }
          }
          if (node.type === 'artifact') {
            // Artifact nodes must be connected to at least one existing phase
            const hasLiveConnection = (updatedWorkflow.connections ?? []).some(
              (c: any) =>
                (c.source === node.id && existingPhaseIds.has(c.target)) ||
                (c.target === node.id && existingPhaseIds.has(c.source))
            );
            if (!hasLiveConnection) {
              console.warn(`🧹 CLIENT-PURGE: ghost artifact node ${node.id} (no live phase connection)`);
              ghostNodeIds.add(node.id);
              return false;
            }
          }
          return true;
        });

        if (ghostNodeIds.size > 0) {
          updatedWorkflow.connections = (updatedWorkflow.connections ?? []).filter(
            (c: any) => !ghostNodeIds.has(c.source) && !ghostNodeIds.has(c.target)
          );
          console.warn(`🧹 CLIENT-PURGE: removed ${ghostNodeIds.size} ghost nodes and their connections`);
        }

        // Deduplicate connections by (source, target, sourceHandle, targetHandle)
        const seenConnKeys = new Set<string>();
        updatedWorkflow.connections = (updatedWorkflow.connections ?? []).filter((c: any) => {
          const key = `${c.source}→${c.target}|${c.sourceHandle}|${c.targetHandle}`;
          if (seenConnKeys.has(key)) {
            console.warn(`🧹 CLIENT-DEDUP: removed duplicate connection ${key}`);
            return false;
          }
          seenConnKeys.add(key);
          return true;
        });
        // ── End ghost node purge ──────────────────────────────────────────────

        console.log('🗨️ CHAT-DEBUG: Calling onWorkflowUpdate with:', updatedWorkflow);

        // Log the workflow DAG structure that came back
        console.log('🔍 WORKFLOW-DAG-FRONTEND: ========== RETURNED WORKFLOW STRUCTURE ==========');
        console.log('🔍 WORKFLOW-DAG-FRONTEND: Phases:', result.updated_workflow?.phases?.length || 0);
        result.updated_workflow?.phases?.forEach((phase, i) => {
          const personas = phase.config?.personas || [];
          console.log(`🔍 WORKFLOW-DAG-FRONTEND: Phase ${i+1}: ${phase.id} (${phase.type}) - ${personas.length} personas`);
          personas.forEach(p => {
            console.log(`🔍 WORKFLOW-DAG-FRONTEND:   - Persona: ${p.persona_id} (${p.role})`);
          });
        });
        console.log('🔍 WORKFLOW-DAG-FRONTEND: ========== END WORKFLOW STRUCTURE ==========');
      
        // Debug tool assignments and nodes
        console.log('🔧 TOOL-DEBUG: ========== DETAILED TOOL ANALYSIS ==========');
        console.log('🔧 TOOL-DEBUG: Raw API response:', result);
        console.log('🔧 TOOL-DEBUG: Updated workflow object:', result.updated_workflow);

        // Check each phase for tool assignments
        result.updated_workflow.phases.forEach((phase, i) => {
          const tools = phase.config?.tools || [];
          console.log(`🔧 TOOL-DEBUG: Phase ${i+1} (${phase.id}):`, {
            label: phase.label,
            type: phase.type,
            toolsAssigned: tools,
            toolCount: tools.length,
            fullConfig: phase.config
          });
        });

        // Check nodes array for tool nodes
        const allNodes = result.updated_workflow.nodes || [];
        const toolNodes = allNodes.filter(n => n.type === 'tool');
        console.log('🔧 TOOL-DEBUG: All nodes in response:', allNodes.length);
        console.log('🔧 TOOL-DEBUG: Tool nodes found:', toolNodes.length);
        console.log('🔧 TOOL-DEBUG: Tool nodes details:', toolNodes);

        // Check connections for tool assignments
        const toolConnections = (result.updated_workflow.connections || []).filter(c => 
          c.dependencyType === 'tool_assignment'
        );
        console.log('🔧 TOOL-DEBUG: Tool assignment connections:', toolConnections.length);
        console.log('🔧 TOOL-DEBUG: Tool connections details:', toolConnections);

        console.log('🔧 TOOL-DEBUG: ========== END TOOL ANALYSIS ==========');
        
        // Log detailed phase analysis
        result.updated_workflow?.phases?.forEach((phase, i) => {
          const personas = phase.config?.personas || [];
          const tools = phase.config?.tools || [];
          console.log(`🔍 PHASE-ANALYSIS: Phase ${i+1} "${phase.label}" - ${personas.length} personas, ${tools.length} tools`);
          personas.forEach(p => console.log(`  - Persona: ${p.persona_id} (${p.role})`));
          tools.forEach(t => console.log(`  - Tool: ${t}`));
        });
        
        onWorkflowUpdate(updatedWorkflow);

        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.response || 'Workflow updated successfully!',
          timestamp: new Date()
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          type: 'error',
          content: result.error || 'Failed to update workflow. Please try again.',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('🗨️ CHAT-DEBUG: API call failed:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'error',
        content: 'Failed to communicate with the server. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, workflowData, onWorkflowUpdate, autoApply, availablePersonas, availableTools]);
  
  // Handle Enter key
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);
  
  if (!show) return null;
  
  return (
    <div 
      ref={tintedBg.ref}
      className={`fixed w-96 rounded-xl shadow-2xl border transition-all duration-300 ${
        isMinimized ? 'h-16' : 'h-[500px]'
      } ${
        darkMode 
          ? 'bg-gray-800/95 border-green-700/50 backdrop-blur-sm' 
          : 'bg-white/95 border-green-300/50 backdrop-blur-sm'
      } flex flex-col`}
      style={{
        ...tintedBg.style,
        zIndex: 9999,
        position: 'fixed',
        bottom: '1rem',
        left: '1rem'
      }}
    >
      {/* Dynamic tinted overlay (COPIED FROM PersonaForge) */}
      <div
        className="absolute inset-0 pointer-events-none rounded-xl"
        style={tintedBg.overlayStyle}
      />
      
      {/* Header - separate from tinted background with its own styling */}
      <div className={`flex items-center justify-between p-4 border-b relative z-30 rounded-t-xl ${
        darkMode 
          ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-gray-600 shadow-lg' 
          : 'bg-gradient-to-r from-white via-gray-50 to-white border-gray-300 shadow-md'
      } backdrop-blur-sm`}>
        <div className="flex items-center">
          <div className={`p-2 rounded-lg mr-3 shadow-md ${
            darkMode 
              ? 'bg-gradient-to-br from-green-600 to-green-700 shadow-green-500/20' 
              : 'bg-gradient-to-br from-green-500 to-green-600 shadow-green-400/30'
          }`}>
            <FiMessageSquare className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className={`font-bold text-sm tracking-wide ${
              darkMode 
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-blue-300' 
                : 'text-transparent bg-clip-text bg-gradient-to-r from-green-700 to-blue-700'
            }`}>
              Chat with Canvas
            </h3>
            <p className={`text-xs font-medium ${
              darkMode 
                ? 'text-gray-300' 
                : 'text-gray-600'
            }`}>
              Modify your workflow with natural language
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Auto-apply toggle with enhanced styling */}
          {!isMinimized && (
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoApply}
                  onChange={(e) => setAutoApply(e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-9 h-5 rounded-full peer transition-all duration-300 ${
                  darkMode 
                    ? 'bg-gray-700 peer-checked:bg-gradient-to-r peer-checked:from-green-600 peer-checked:to-green-500 shadow-inner' 
                    : 'bg-gray-300 peer-checked:bg-gradient-to-r peer-checked:from-green-500 peer-checked:to-green-400 shadow-inner'
                } peer-focus:ring-2 peer-focus:ring-green-300`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow-lg transform transition-all duration-300 ${
                    autoApply ? 'translate-x-4 shadow-green-200' : 'translate-x-0.5'
                  } mt-0.5`}></div>
                </div>
              </label>
              <span className={`ml-2 text-xs font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Auto-apply
              </span>
            </div>
          )}
          
          {/* Control buttons with enhanced styling */}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className={`p-1.5 rounded-full transition-all duration-200 ${
              darkMode 
                ? 'hover:bg-gray-700 hover:shadow-lg hover:shadow-gray-500/20' 
                : 'hover:bg-gray-200 hover:shadow-md hover:shadow-gray-300/30'
            }`}
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? <FiMaximize2 className="h-4 w-4" /> : <FiMinimize2 className="h-4 w-4" />}
          </button>
          
          <button
            onClick={onToggle}
            className={`p-1.5 rounded-full transition-all duration-200 ${
              darkMode 
                ? 'hover:bg-red-900/50 hover:shadow-lg hover:shadow-red-500/20 text-red-400' 
                : 'hover:bg-red-100 hover:shadow-md hover:shadow-red-300/30 text-red-600'
            }`}
            title="Close Chat"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Messages with gradient fade effect */}
      {!isMinimized && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Gradient fade overlay - fade from top to bottom */}
          <div className={`absolute inset-0 pointer-events-none z-20 ${
            darkMode 
              ? 'bg-gradient-to-b from-gray-800/95 via-transparent to-transparent' 
              : 'bg-gradient-to-b from-white/95 via-transparent to-transparent'
          }`} />
          
          {/* Messages container with FIXED height */}
          <div 
            className="flex-1 overflow-y-auto p-4 space-y-3 relative z-10"
            style={{ 
              height: '360px',
              maxHeight: '360px'
            }}
          >
            {/* Normal message order - oldest to newest */}
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.type === 'user'
                      ? darkMode 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-500 text-white'
                      : message.type === 'error'
                        ? darkMode 
                          ? 'bg-red-900/50 text-red-300 border border-red-800' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                        : message.type === 'system'
                          ? darkMode 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                          : darkMode 
                            ? 'bg-gray-700 text-white' 
                            : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  <div className={`text-xs mt-1 opacity-70`}>
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className={`p-3 rounded-lg ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                    <span className="text-sm">Updating workflow...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
      
      {/* Input */}
      {!isMinimized && (
        <div className={`p-4 border-t relative z-10 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex space-x-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe how you want to modify your workflow..."
              className={`flex-1 p-2 rounded-md border resize-none ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent`}
              rows={2}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || availablePersonas.length === 0 || availableTools.length === 0}
              className={`px-3 py-2 rounded-md ${
                !inputValue.trim() || isLoading || availablePersonas.length === 0 || availableTools.length === 0
                  ? darkMode 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : darkMode 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
              } transition-colors`}
            >
              <FiSend className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
